import { FC, useState } from 'react';
import { Divider, Menu, MenuItem, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { NodeObject } from 'react-force-graph-2d';
import { IAffiliationStatistics } from '@models/IAffiliationStatistics';
import { StatsApi } from '@api/organizations/stats';
// eslint-disable-next-line import/no-cycle
import { AddNode } from '../AddNode';
import { OrganizationSummary } from '../OrganizationSummary';

export type NodeType = 'manual' | 'existing';

interface NodeContextMenuProps {
  title: string;
  nodes: NodeObject[];
  nodeContext: { node: NodeObject; xy: [number, number] };
  onClose: () => void;
  onNodeChosen: (source: string, target: string, type: NodeType) => void;
  onRemoveNodeClick: (node: NodeObject) => void;
}

export const NodeContextMenu: FC<NodeContextMenuProps> = ({
  title,
  nodes,
  nodeContext,
  onClose,
  onNodeChosen,
  onRemoveNodeClick,
}) => {
  const [openAddNode, setOpenAddNode] = useState(false);
  const [affiliationStats, setAffiliationStats] =
    useState<IAffiliationStatistics>(null);

  const onViewData = async () => {
    setAffiliationStats(null);

    StatsApi.getAffiliation(title).then((res) => {
      setAffiliationStats({
        ...res.data.data[0].source,
        keywords: res.data.data[0].source.keywords.slice(0, 300),
      });
    });
  };

  return (
    <>
      <AddNode
        title={`Link "${title}" to...`}
        nodeName={nodeContext?.node?.id as string}
        nodes={nodes}
        open={openAddNode}
        onClose={() => {
          onClose();
          setAffiliationStats(null);
          setOpenAddNode(false);
        }}
        chosenNode={(source, target, type) => {
          onNodeChosen(source, target, type);
        }}
      />
      <Menu
        open={Boolean(nodeContext)}
        anchorReference={'anchorPosition'}
        anchorPosition={{
          top: nodeContext?.xy[1],
          left: nodeContext?.xy[0],
        }}
        onClose={() => {
          setAffiliationStats(null);
          onClose();
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(8,12,30,0.97)',
            border: '1px solid rgba(99,102,241,0.3)',
            color: '#e5e7eb',
            '& .MuiMenuItem-root': {
              color: '#c5cae9',
              '&:hover': {
                backgroundColor: 'rgba(99,102,241,0.15)',
              },
            },
            '& .MuiDivider-root': {
              borderColor: 'rgba(99,102,241,0.2)',
            },
          },
        }}
      >
        {affiliationStats ? (
          <OrganizationSummary
            title={title}
            affiliationStats={affiliationStats}
            loading={!affiliationStats}
          />
        ) : (
          <>
            <Typography
              sx={{
                px: 2,
                fontWeight: 600,
                whiteSpace: 'pre-line',
                hyphens: 'auto',
                textAlign: 'center',
                pb: 0.5,
                maxWidth: 250,
                color: '#c5cae9',
                fontSize: 13,
                letterSpacing: '0.01em',
              }}
            >
              {title}
            </Typography>
            <Divider />
            {['Affiliations', 'Impact Affiliations'].includes(
              // @ts-ignore
              nodeContext?.node?.expansion,
            ) && (
              <MenuItem onClick={onViewData}>
                <Icon
                  icon="akar-icons:info-fill"
                  style={{ marginRight: 8 }}
                  color={'#6366f1'}
                />
                <>View Data</>
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                setOpenAddNode(true);
              }}
            >
              <Icon
                icon="carbon:add-filled"
                style={{ marginRight: 8 }}
                color={'#6366f1'}
              />
              <>Add Link</>
            </MenuItem>
            <MenuItem
              onClick={() => {
                onRemoveNodeClick(nodeContext.node);
              }}
            >
              <Icon
                icon="clarity:remove-solid"
                style={{ marginRight: 8 }}
                color={'#ef4444'}
              />
              <>Remove Node</>
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};
